import React, { memo, useCallback, useMemo } from 'react';

import { getAssetFromItem } from '../../assets/utils';
import { FEATURE_KEEP_ASPECT_RATIO_CONTROL } from '../../flags';
import { EditorStarterItem } from '../../items/item-type';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import {
  updateItemHeight,
  updateItemWidth,
} from '../../state/actions/update-item-dimensions';
import {
  canKeepAspectRatioMap,
  getKeepAspectRatio,
  getOriginalAspectRatio,
} from '../../utils/aspect-ratio';
import { getRectAfterCrop } from '../../utils/get-dimensions-after-crop';
import { useItem, useWriteContext } from '../../utils/use-context';
import { InspectorSubLabel } from '../components/inspector-label';
import { useSelectionValues } from '../utils/use-selection-values';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { ControlsPadding } from './controls-padding';
import { KeepAspectRatioControls } from './keep-aspect-ratio-controls';
import { NumberControlUpdateHandler } from './number-controls';
import { ValueInputField } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';

// 아이템에서 crop 적용 후 width 추출
const getWidthFromItem = (item: EditorStarterItem): number => {
  return Math.floor(getRectAfterCrop(item).width);
};

// 아이템에서 crop 적용 후 height 추출
const getHeightFromItem = (item: EditorStarterItem): number => {
  return Math.floor(getRectAfterCrop(item).height);
};

// 아이템에서 keepAspectRatio 값 추출
const getKeepAspectRatioFromItem = (item: EditorStarterItem): boolean => {
  return getKeepAspectRatio(item);
};

const DimensionsControlsUnmemoized: React.FC<{
  itemId: string;
}> = ({ itemId }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const item = useItem(itemId);

  const targetIds = useTargetItemIds(itemId);
  const { isMixed: isWidthMixed } = useSelectionValues<
    EditorStarterItem,
    number
  >(itemId, getWidthFromItem);
  const { isMixed: isHeightMixed } = useSelectionValues<
    EditorStarterItem,
    number
  >(itemId, getHeightFromItem);
  const { isMixed: isKeepAspectRatioMixed } = useSelectionValues<
    EditorStarterItem,
    boolean
  >(itemId, getKeepAspectRatioFromItem);

  const rectAfterCrop = useMemo(() => {
    if (!item) return { left: 0, top: 0, width: 0, height: 0 };
    return getRectAfterCrop(item);
  }, [item]);

  const onHeight: NumberControlUpdateHandler = useCallback(
    ({ num: heightAfterCrop, commitToUndoStack }) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            const currentRectAfterCrop = getRectAfterCrop(i);
            const diff = i.height - currentRectAfterCrop.height;
            const num = heightAfterCrop + diff;

            const keepAspectRatio = getKeepAspectRatio(i);
            if (!keepAspectRatio) {
              return updateItemHeight({
                item: i,
                height: num,
                stopTextEditing: true,
              });
            }

            const asset = getAssetFromItem({
              item: i,
              assets: state.undoableState.assets,
            });
            const aspectRatio = getOriginalAspectRatio({ item: i, asset });
            const newWidth = Math.round(num * aspectRatio);

            const updatedItem = updateItemHeight({
              item: i,
              height: num,
              stopTextEditing: true,
            });
            return updateItemWidth({
              item: updatedItem,
              width: newWidth,
              stopTextEditing: true,
            });
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  const onWidth: NumberControlUpdateHandler = useCallback(
    ({ num: widthAfterCrop, commitToUndoStack }) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            const currentRectAfterCrop = getRectAfterCrop(i);
            const diff = i.width - currentRectAfterCrop.width;
            const num = widthAfterCrop + diff;

            const keepAspectRatio = getKeepAspectRatio(i);
            if (!keepAspectRatio) {
              return updateItemWidth({
                item: i,
                width: num,
                stopTextEditing: true,
              });
            }

            const asset = getAssetFromItem({
              item: i,
              assets: state.undoableState.assets,
            });
            const aspectRatio = getOriginalAspectRatio({ item: i, asset });
            const newHeight = Math.round(num / aspectRatio);

            const updatedItem = updateItemWidth({
              item: i,
              width: num,
              stopTextEditing: true,
            });
            return updateItemHeight({
              item: updatedItem,
              height: newHeight,
              stopTextEditing: true,
            });
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  if (!item) {
    return null;
  }

  return (
    <div>
      <InspectorSubLabel>
        {t('Editor.inspector.text.dimensions')}
      </InspectorSubLabel>
      <div className="flex w-full flex-row gap-4xs">
        <div className="flex-1">
          <ValueInputField
            isShowLeadingNode={true}
            leadingNodeType="text"
            leadingNodeValue="W"
            setValue={onWidth}
            value={Math.floor(rectAfterCrop.width)}
            min={1}
            max={null}
            step={1}
            accessibilityLabel="Width"
            hasMixedValue={isWidthMixed}
          />
        </div>
        <div className="flex-1">
          <ValueInputField
            isShowLeadingNode={true}
            leadingNodeType="text"
            leadingNodeValue="H"
            setValue={onHeight}
            value={Math.floor(rectAfterCrop.height)}
            min={1}
            max={null}
            step={1}
            accessibilityLabel="Height"
            hasMixedValue={isHeightMixed}
          />
        </div>
        {FEATURE_KEEP_ASPECT_RATIO_CONTROL &&
        canKeepAspectRatioMap[item.type] ? (
          <KeepAspectRatioControls
            keepAspectRatio={getKeepAspectRatio(item)}
            itemId={itemId}
            isMixed={isKeepAspectRatioMixed}
          />
        ) : (
          <ControlsPadding />
        )}
      </div>
    </div>
  );
};

export const DimensionsControls = memo(DimensionsControlsUnmemoized);
